import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from "react-router-dom"
import styled from 'styled-components';
import { MdOfflineBolt } from "react-icons/md";

import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

function DeviceList({
    jsonData,
    handleDeviceSelect,
    totalDevices,
    currentPage,
    onPageChange,
    isLoading
}) {

    const navigate = useNavigate()

    const refContainer = useRef();
    const [dimensions, setDimensions] = useState({
        width: 0,
        height: 0,
    });
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [loadingFinish, setloadingFinish] = useState(false);

    // Calcular itemsPerPage basado en la altura del contenedor
    const calculateItemsPerPage = (height) => {
        const headerHeight = 54;
        const rowHeight = 54;
        const maxRows = Math.floor((height - headerHeight) / rowHeight);
        return Math.max(1, maxRows);
    };

    useEffect(() => {
        setTimeout(() => {
            setloadingFinish(true);
        }, 1000);
        if (refContainer.current) {
            const newDimensions = {
                width: refContainer.current.offsetWidth,
                height: refContainer.current.offsetHeight,
            };
            setDimensions(newDimensions);
            const calculatedItems = calculateItemsPerPage(newDimensions.height);
            setItemsPerPage(calculatedItems);
            onPageChange(1, calculatedItems);
        }
    }, []);

    // Listener para recargar la página cuando cambia el tamaño de la ventana
    useEffect(() => {
        let resizeTimeout;
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                window.location.reload();
            }, 300);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeout);
        };
    }, []);

    const boltColors = ['#ff3a30d7', '#f1c40f', '#2ecc71'];
    const statusName = ['Inactivo', 'Alerta', 'Activo'];
    const statusClass = ['inactive', 'warning', 'active'];

    // Calcular total de páginas y rango de dispositivos mostrados
    const totalPages = Math.ceil(totalDevices / itemsPerPage) || 1;
    const startItem = totalDevices > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalDevices);

    // Generar números de página a mostrar
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Mostrar todas las páginas si son pocas
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Lógica para mostrar páginas con ellipsis
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const loadSkeleton = () => {
        const maxCells = Math.floor((dimensions.height - 54) / 54);

        return Array.from({ length: maxCells }, (_, i) => (
            <Skeleton
                key={i}
                height={40}
                width={`${dimensions.width * 0.95}px`}
                borderRadius={0}
            />
        ));
    };

    const createFileds = (json) => {
        //<tr key={index} onClick={() => navigate(`/home/devices/${hwid}`)}>
        const maxCells = parseInt((dimensions.height - 54) / 54);
        return json.map(({ hwid, status, client_info, meter_id }, index) => {
            const desc = client_info || {};
            const currentColor = boltColors[status];
            if (index >= maxCells) return (null);
            return (
                <tr key={index} onClick={() => handleDeviceSelect({ selected: true, device: json[index] })}>
                    <DeviceCell $currentColor={currentColor}> <MdOfflineBolt /> {meter_id || hwid}</DeviceCell>
                    <DeviceCell ><span className={`status ${statusClass[status]}`}>{statusName[status]}</span></DeviceCell>
                    <DeviceCell>{desc.city ? desc.city : 'No seteado'}</DeviceCell>
                    <DeviceCell>{desc.client ? desc.client : 'No seteado'}</DeviceCell>
                    <DeviceCell>{desc.address ? desc.address : 'No seteado'}</DeviceCell>
                </tr>
            );
        });
    }

    return (
        <Container>
            <List ref={refContainer}>
                <table id="device_table">
                    <thead>
                        <tr>
                            <th>N° Medidor</th>
                            <th>Estado</th>
                            <th>Ciudad</th>
                            <th>Cliente</th>
                            <th>Dirección</th>
                        </tr>
                        <tr style={{ display: 'none' }}>
                            <th><input type="text" placeholder="Búsqueda" /></th>
                            <th><input type="text" placeholder="Búsqueda" /></th>
                            <th><input type="text" placeholder="Búsqueda" /></th>
                            <th><input type="text" placeholder="Búsqueda" /></th>
                            <th><input type="text" placeholder="Búsqueda" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(loadingFinish && !isLoading) && createFileds(jsonData)}
                    </tbody>
                </table>
                {(!loadingFinish || isLoading) && loadSkeleton()}
            </List>
            <Pagination>
                <span>
                    Mostrando {startItem} hasta {endItem} de {totalDevices} dispositivos
                </span>
                <div className='pages'>
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        {'<'}
                    </button>
                    {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                            <span key={`ellipsis-${index}`} className="ellipsis">...</span>
                        ) : (
                            <span
                                key={page}
                                className={currentPage === page ? 'active' : ''}
                                onClick={() => onPageChange(page)}
                            >
                                {page}
                            </span>
                        )
                    ))}
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        {'>'}
                    </button>
                </div>
            </Pagination>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 100%;
    min-height: 400px;
    height: 80vh;
    border-radius: 8px;
    overflow: hidden;
    font-family: 'Segoe UI', sans-serif;
    background: transparent;
`

const List = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 90%;
    border-radius: 8px;
    overflow: hidden;
    font-family: 'Segoe UI', sans-serif;
    background: #f8f9fb;
    background-color: #fff;


    table {
        width: 95%;
        border-collapse: collapse;
    }

    tbody tr:hover{
        background: #f8f8f8;
        cursor: pointer;
    }

    th{
        height: 54px;
        text-align: left;
        font-size: 14px;
    }

    input[type="text"] {
        display: flex;
        width: 8.4375rem;
        height: 2rem;
        align-items: center;
        padding: 0 0.625rem;
        text-align: center;

        border-radius: 0.5rem;
        border: 1px solid rgba(0, 0, 0, 0.15);
        background-color: #F8F8F8;
    }
`

const DeviceCell = styled.td`
    height: 54px;
    text-align: left;
    font-size: 14px;

    svg{
        font-size: 24px;
        vertical-align: middle;
        color: ${(props) => props.$currentColor};
    }

    .status{
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        border-radius: 20px;
        padding: 4px 10px;
        width: 70%;

        &.active {
            background-color: #d4f5d3;
            color: #2ecc71;
        }

        &.warning {
            background-color: #fff4d3;
            color: #f1c40f;
        }

        &.inactive {
            background-color: #FF3B304D;
            color: #ff3a30d7;
        }
    }
`

const Pagination = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-size: 14px;

    .pages{
        display: flex;
        gap: 5px;

        span, button{
            padding: 6px 10px;
            border: none;
            border-radius: 4px;
            background-color: #eee;
            cursor: pointer;
            transition: background-color 0.2s;

            &:hover:not(:disabled):not(.ellipsis) {
                background-color: #ddd;
            }
        }

        button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .active{
            background-color: #9b59b6;
            color: white;
        }

        .ellipsis {
            cursor: default;
            background-color: transparent;
        }
    }
`

export default DeviceList
